/**
 * Hand-drawn SVG illustrations for the landing page — isometric wireframe
 * style (Vercel-dark friendly): translucent faces, 1px light strokes, dashed
 * animated connectors. Everything is inline SVG; no images, no libraries.
 *
 * The isometric projection: x runs to the lower-right, y to the lower-left,
 * z straight up. `isoPt` maps (x, y, z) into screen space around a pivot.
 */

const COS = 0.866; // cos(30°)

function isoXY(px: number, py: number, x: number, y: number, z: number): [number, number] {
  return [px + (x - y) * COS, py + (x + y) * 0.5 - z];
}

function isoPt(px: number, py: number, x: number, y: number, z: number): string {
  const [sx, sy] = isoXY(px, py, x, y, z);
  return `${sx.toFixed(1)},${sy.toFixed(1)}`;
}

const FACE_TOP = "rgba(109,107,247,0.20)";
const FACE_LEFT = "rgba(255,255,255,0.05)";
const FACE_RIGHT = "rgba(109,107,247,0.09)";
const STROKE = "rgba(255,255,255,0.30)";
const MUTED = "#a1a1a1";
const ACCENT = "#6d6bf7";
const ACCENT2 = "#8b89f9";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/** Wireframe isometric box: three visible faces around pivot (px, py). */
function IsoBox({
  px,
  py,
  w,
  d,
  h,
  topFill = FACE_TOP,
}: {
  px: number;
  py: number;
  w: number;
  d: number;
  h: number;
  topFill?: string;
}) {
  const p = (x: number, y: number, z: number) => isoPt(px, py, x, y, z);
  return (
    <g stroke={STROKE} strokeWidth="1" strokeLinejoin="round">
      <polygon points={`${p(0, 0, h)} ${p(w, 0, h)} ${p(w, d, h)} ${p(0, d, h)}`} fill={topFill} />
      <polygon points={`${p(0, 0, 0)} ${p(0, 0, h)} ${p(0, d, h)} ${p(0, d, 0)}`} fill={FACE_LEFT} />
      <polygon points={`${p(0, 0, 0)} ${p(w, 0, 0)} ${p(w, 0, h)} ${p(0, 0, h)}`} fill={FACE_RIGHT} />
    </g>
  );
}

/** Flat isometric tile (a box with minimal height) — used as ground plates. */
function IsoTile({
  px,
  py,
  w,
  d,
  fill = "rgba(255,255,255,0.04)",
}: {
  px: number;
  py: number;
  w: number;
  d: number;
  fill?: string;
}) {
  const p = (x: number, y: number) => isoPt(px, py, x, y, 0);
  return (
    <polygon
      points={`${p(0, 0)} ${p(w, 0)} ${p(w, d)} ${p(0, d)}`}
      fill={fill}
      stroke={STROKE}
      strokeWidth="1"
    />
  );
}

/** QR module pattern drawn onto an isometric top face. */
function IsoQr({ px, py, size }: { px: number; py: number; size: number }) {
  const cell = size / 7;
  const PATTERN = [
    [1, 1, 1, 0, 1, 1, 1],
    [1, 0, 1, 0, 1, 0, 1],
    [1, 1, 1, 0, 1, 1, 1],
    [0, 0, 0, 1, 0, 0, 0],
    [1, 1, 0, 1, 1, 0, 1],
    [1, 0, 1, 0, 0, 1, 1],
    [1, 1, 0, 1, 1, 0, 1],
  ];
  const cells: string[] = [];
  PATTERN.forEach((row, iy) =>
    row.forEach((v, ix) => {
      if (!v) return;
      const x0 = ix * cell;
      const y0 = iy * cell;
      cells.push(
        `${isoPt(px, py, x0, y0, 0)} ${isoPt(px, py, x0 + cell, y0, 0)} ${isoPt(px, py, x0 + cell, y0 + cell, 0)} ${isoPt(px, py, x0, y0 + cell, 0)}`,
      );
    }),
  );
  return (
    <g fill="#ededed">
      {cells.map((pts) => (
        <polygon key={pts} points={pts} />
      ))}
    </g>
  );
}

const L10N = {
  tr: {
    product: "ÜRÜN",
    carrier: "QR / NFC",
    passport: "PASAPORT",
    signed: "imzalı · sürümlü",
    consumer: "TÜKETİCİ",
    customs: "GÜMRÜK",
    recycler: "GERİ DÖNÜŞÜMCÜ",
    data: "ÜRÜN VERİSİ",
    publish: "YAYIMLA",
    versions: "v1 → v2 → v3",
    scan: "TARA",
  },
  en: {
    product: "PRODUCT",
    carrier: "QR / NFC",
    passport: "PASSPORT",
    signed: "signed · versioned",
    consumer: "CONSUMER",
    customs: "CUSTOMS",
    recycler: "RECYCLER",
    data: "PRODUCT DATA",
    publish: "PUBLISH",
    versions: "v1 → v2 → v3",
    scan: "SCAN",
  },
} as const;

function Label({
  x,
  y,
  children,
  fill = MUTED,
  size = 10,
}: {
  x: number;
  y: number;
  children: string;
  fill?: string;
  size?: number;
}) {
  return (
    <text
      x={x}
      y={y}
      fill={fill}
      fontSize={size}
      fontFamily={MONO}
      letterSpacing="0.08em"
      textAnchor="middle"
    >
      {children}
    </text>
  );
}

/**
 * Section 01 hero diagram: a single data packet is minted at the product,
 * carried by the QR/NFC tag, and lands on the signed passport — which then
 * serves each role (consumer / customs / recycler) its own layer. The blue
 * packet flows product→carrier→passport; three green packets fan back out to
 * the role chips, staggered, so the eye reads the sequence rather than a wall
 * of uniform dashes. Motion is CSS-only (`.ld-pulse`, disabled under
 * prefers-reduced-motion); the dashed rails are static structure underneath.
 */
export function FlowIllustration({ lang }: { lang: "tr" | "en" }) {
  const t = L10N[lang];
  const f = (n: number) => n.toFixed(1);

  // Passport slab pivot; ports are the left edge of a row at height z.
  const PX = 452;
  const PY = 150;
  const port = (z: number) => isoXY(PX, PY, 10, 0, z);
  const portR = (z: number) => isoXY(PX, PY, 78, 0, z);
  const ROW_Z = [98, 76, 54, 32] as const; // header, then one row per role
  const [cz, uz, rz] = [ROW_Z[1], ROW_Z[2], ROW_Z[3]];

  // Main flow: product → QR carrier → passport base (the blue packet's track).
  const MAIN_D = "M140 236 C 196 252, 228 268, 262 270 C 332 274, 402 186, 452 152";

  // One green branch per role: from its passport row out to the chip. Path is
  // authored port→chip so the packet travels outward (offset-distance 0→100%).
  const roles = [
    { label: t.consumer, chip: { x: 92, cx: 148 }, from: port(cz), mid1: [360, 92], mid2: [250, 92] },
    { label: t.customs, chip: { x: 216, cx: 272 }, from: port(uz), mid1: [388, 96], mid2: [320, 78] },
    { label: t.recycler, chip: { x: 340, cx: 396 }, from: port(rz), mid1: [436, 108], mid2: [420, 84] },
  ].map((r) => ({
    ...r,
    d: `M${f(r.from[0])} ${f(r.from[1])} C ${r.mid1[0]} ${r.mid1[1]}, ${r.mid2[0]} ${r.mid2[1]}, ${r.chip.cx} 62`,
  }));

  return (
    <svg className="ld-illus" viewBox="0 0 560 340" role="img" aria-label={t.passport}>
      {/* ground plates */}
      <IsoTile px={60} py={206} w={104} d={104} />
      <IsoTile px={250} py={244} w={84} d={84} />
      <IsoTile px={428} py={176} w={96} d={122} />

      {/* static rails: role connectors (drawn first so nodes sit on top) */}
      {roles.map((r) => (
        <path key={r.label} className="ld-wire" d={r.d} fill="none" />
      ))}
      <path className="ld-wire ld-wire-accent" d={MAIN_D} fill="none" />

      {/* product box */}
      <IsoBox px={78} py={192} w={66} d={66} h={52} />
      <Label x={78} y={284}>
        {t.product}
      </Label>

      {/* QR carrier tile */}
      <IsoTile px={262} py={236} w={60} d={60} fill="#fff" />
      <IsoQr px={266} py={238} size={52} />
      <Label x={262} y={308}>
        {t.carrier}
      </Label>

      {/* passport panel: a thin standing slab with content rows */}
      <IsoBox px={PX} py={PY} w={88} d={10} h={118} topFill={FACE_RIGHT} />
      {ROW_Z.map((z, i) => {
        const [x1, y1] = port(z);
        const [x2, y2] = portR(z);
        return (
          <line
            key={z}
            x1={f(x1)}
            y1={f(y1)}
            x2={f(x2)}
            y2={f(y2)}
            stroke={i === 0 ? ACCENT : "rgba(109,107,247,0.55)"}
            strokeWidth={i === 0 ? 2 : 1.25}
          />
        );
      })}
      {/* role port nubs — where each green packet departs the passport */}
      {[cz, uz, rz].map((z) => {
        const [x, y] = port(z);
        return <circle key={z} cx={f(x)} cy={f(y)} r={2.4} fill={ACCENT2} opacity={0.85} />;
      })}
      <circle
        className="ld-beacon"
        cx={f(isoXY(PX, PY, 62, 5, 118)[0])}
        cy={f(isoXY(PX, PY, 62, 5, 118)[1])}
        r={4}
        fill={ACCENT2}
      />
      <Label x={496} y={214}>
        {t.passport}
      </Label>
      <Label x={496} y={228} size={9}>
        {t.signed}
      </Label>

      {/* role chips */}
      {roles.map((r, i) => (
        <g key={r.label}>
          <rect
            x={r.chip.x}
            y={36}
            width={112}
            height={26}
            rx={13}
            fill="rgba(255,255,255,0.04)"
            stroke={STROKE}
          />
          <circle
            cx={r.chip.x + 16}
            cy={49}
            r={3}
            fill={ACCENT2}
            className="ld-node"
            style={{ animationDelay: `${1.2 + i * 0.18}s` }}
          />
          <Label x={r.chip.cx + 8} y={53} size={9}>
            {r.label}
          </Label>
        </g>
      ))}

      {/* Packets: a continuous stream in (product→passport), then two staggered
          waves fanning out to the role chips — so the flow never reads as idle. */}
      {[0, 1.2, 2.4].map((delay) => (
        <circle
          key={`in-${delay}`}
          className="ld-pulse ld-pulse-in"
          r={3.6}
          style={{ offsetPath: `path("${MAIN_D}")`, animationDelay: `${delay}s` }}
        />
      ))}
      {roles.map((r, i) =>
        [1.15, 2.95].map((base) => (
          <circle
            key={`${r.label}-${base}`}
            className="ld-pulse ld-pulse-out"
            r={3.1}
            style={{ offsetPath: `path("${r.d}")`, animationDelay: `${base + i * 0.18}s` }}
          />
        )),
      )}
    </svg>
  );
}

/** Section 02 step scenes — one compact isometric vignette per step. */
export function StepIllustration({ kind, lang }: { kind: "data" | "publish" | "scan"; lang: "tr" | "en" }) {
  const t = L10N[lang];
  if (kind === "data") {
    // form panel: standing slab with field rows, one highlighted
    return (
      <svg className="ld-illus ld-illus-step" viewBox="0 0 220 130" aria-hidden="true">
        <IsoTile px={68} py={92} w={80} d={80} />
        <IsoBox px={82} py={84} w={72} d={8} h={64} topFill={FACE_RIGHT} />
        {[0, 1, 2].map((i) => {
          const [x1, y1] = isoPt(82, 84, 8, 0, 52 - i * 16).split(",");
          const [x2, y2] = isoPt(82, 84, 64, 0, 52 - i * 16).split(",");
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={i === 1 ? ACCENT2 : STROKE}
              strokeWidth={i === 1 ? 2 : 1}
            />
          );
        })}
        <Label x={110} y={122}>
          {t.data}
        </Label>
      </svg>
    );
  }
  if (kind === "publish") {
    // version stack + hash chain
    return (
      <svg className="ld-illus ld-illus-step" viewBox="0 0 220 130" aria-hidden="true">
        <IsoTile px={64} py={94} w={90} d={90} />
        <IsoBox px={78} py={92} w={62} d={62} h={10} />
        <IsoBox px={84} py={78} w={50} d={50} h={10} />
        <IsoBox px={90} py={64} w={38} d={38} h={10} topFill="rgba(52,211,153,0.22)" />
        <circle cx={110} cy={38} r={9} fill="none" stroke={ACCENT2} strokeWidth="1.5" />
        <path d="M106 38l3 3 5-6" fill="none" stroke={ACCENT2} strokeWidth="1.5" strokeLinecap="round" />
        <Label x={110} y={122}>
          {t.versions}
        </Label>
      </svg>
    );
  }
  // scan: phone slab + beam to QR tile
  return (
    <svg className="ld-illus ld-illus-step" viewBox="0 0 220 130" aria-hidden="true">
      <IsoTile px={58} py={96} w={56} d={56} fill="#fff" />
      <IsoQr px={62} py={97} size={48} />
      <IsoBox px={158} py={62} w={30} d={6} h={54} topFill={FACE_RIGHT} />
      <path d="M96 78 L152 40 L162 92 Z" fill="rgba(109,107,247,0.10)" stroke="none" />
      <path
        className="ld-dash"
        d="M96 78 L152 40 M96 78 L162 92"
        fill="none"
        stroke={ACCENT}
        strokeWidth="1"
      />
      <Label x={110} y={122}>
        {t.scan}
      </Label>
    </svg>
  );
}

/* ------------------------------------------------------------ mini icons */

export type MiniIconName =
  | "layers"
  | "droplet"
  | "pin"
  | "branch"
  | "bars"
  | "loop"
  | "rosette"
  | "star"
  | "lock"
  | "pen"
  | "link"
  | "braces"
  | "bank"
  | "archive"
  | "flask"
  | "globe"
  | "tag";

const ICON_PATHS: Record<MiniIconName, React.ReactNode> = {
  layers: (
    <>
      <path d="M12 3l8 4.5-8 4.5-8-4.5L12 3Z" />
      <path d="M4 12l8 4.5 8-4.5M4 16.5 12 21l8-4.5" />
    </>
  ),
  droplet: <path d="M12 3c3.5 4.2 6 7.2 6 10.2a6 6 0 1 1-12 0C6 10.2 8.5 7.2 12 3Z" />,
  pin: (
    <>
      <path d="M12 21s-6.5-5.6-6.5-10.5a6.5 6.5 0 1 1 13 0C18.5 15.4 12 21 12 21Z" />
      <circle cx="12" cy="10.5" r="2.2" />
    </>
  ),
  branch: (
    <>
      <circle cx="6" cy="5" r="2" />
      <circle cx="18" cy="19" r="2" />
      <path d="M6 7v5a5 5 0 0 0 5 5h5" />
    </>
  ),
  bars: <path d="M5 20v-7M10 20V5M15 20v-9M20 20V8" />,
  loop: (
    <>
      <path d="M5 12a7 7 0 0 1 12-4.5M19 12a7 7 0 0 1-12 4.5" />
      <path d="M17 4v3.5h-3.5M7 20v-3.5h3.5" />
    </>
  ),
  rosette: (
    <>
      <circle cx="12" cy="9" r="5" />
      <path d="M9.2 13.2 8 21l4-2.5L16 21l-1.2-7.8" />
    </>
  ),
  star: <path d="M12 4l2.2 4.9 5.3.5-4 3.6 1.2 5.2L12 15.5 7.3 18.2l1.2-5.2-4-3.6 5.3-.5L12 4Z" />,
  lock: (
    <>
      <rect x="6" y="11" width="12" height="9" rx="2" />
      <path d="M9 11V7.5a3 3 0 0 1 6 0V11" />
    </>
  ),
  pen: <path d="M4.5 19.5l3.8-1L19.5 7.3a1.9 1.9 0 0 0-2.7-2.7L5.6 15.8l-1.1 3.7Z" />,
  link: (
    <>
      <path d="M9.5 14.5l5-5" />
      <path d="M8.5 11.5 6.6 13.4a3.4 3.4 0 0 0 4.8 4.8l1.9-1.9M15.5 12.5l1.9-1.9a3.4 3.4 0 0 0-4.8-4.8l-1.9 1.9" />
    </>
  ),
  braces: (
    <>
      <path d="M9 4.5C7.3 4.5 6.5 5.5 6.5 7v2c0 1.3-.8 2.2-2 2.5 1.2.3 2 1.2 2 2.5v3c0 1.5.8 2.5 2.5 2.5" />
      <path d="M15 4.5c1.7 0 2.5 1 2.5 2.5v2c0 1.3.8 2.2 2 2.5-1.2.3-2 1.2-2 2.5v3c0 1.5-.8 2.5-2.5 2.5" />
    </>
  ),
  bank: (
    <>
      <path d="M4 9.5 12 4l8 5.5H4Z" />
      <path d="M5.5 9.5V18M9.8 9.5V18M14.2 9.5V18M18.5 9.5V18M4 18h16" />
    </>
  ),
  archive: (
    <>
      <rect x="4" y="5" width="16" height="4.5" rx="1" />
      <path d="M6 9.5V19h12V9.5M10 13h4" />
    </>
  ),
  flask: (
    <>
      <path d="M10 3.5v5.6l-4.7 8.4A1.8 1.8 0 0 0 6.9 20h10.2a1.8 1.8 0 0 0 1.6-2.5L14 9.1V3.5" />
      <path d="M8.5 3.5h7M8 14.5h8" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c3.2 3.2 3.2 13.8 0 17M12 3.5c-3.2 3.2-3.2 13.8 0 17" />
    </>
  ),
  tag: (
    <>
      <path d="M3.5 12.5 12 21l8.5-8.5L12 4H6a2.5 2.5 0 0 0-2.5 2.5v6Z" />
      <circle cx="8.5" cy="8.5" r="1.4" />
    </>
  ),
};

/** Small stroke icon for feature/standard/diff cells. */
export function MiniIcon({ name }: { name: MiniIconName }) {
  return (
    <svg
      className="f-ico"
      viewBox="0 0 24 24"
      width="26"
      height="26"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}
